import { colord } from 'colord';
import type { ButtonOwnProps, ButtonProps, ChipOwnProps } from '@mui/material';
import type { PaletteColor } from '@mui/material/styles';
import type { TypographyStyle } from '@mui/material/styles/createTypography';

import { DESIGN_ENUMS } from '@/modules/ChannelList/constants/carousels';
import {
  configurationSvgSizes,
  CUSTOM_CONFIGURATION_OPTION_PREFIX,
  LIKE_KEY,
  SHARE_KEY,
  VIEW_KEY,
} from '@/modules/common/constants/configuration';
import { defaultDateTimeFormat, supportedLanguages } from '@/modules/common/constants/languages';
import {
  SHAPE_CIRCULAR,
  SHAPE_ROUNDED,
  SHAPE_SQUARE,
  SIZE_LARGE,
  SIZE_MEDIUM,
  SIZE_SMALL,
  SIZE_X_LARGE,
  SIZE_X_SMALL,
  VARIANT_CONTAINED,
  VARIANT_FILLED,
  VARIANT_OUTLINED,
  VARIANT_TEXT,
} from '@/mui/constants';

import { ChannelConfig, ConfigurationIconType, ConfigurationType } from '../types/configuration';

import logger from '@/modules/common/helpers/logger';

/**
 * Processes external configuration and merges it into default structure.
 * Deeply freezes the result in development mode to prevent accidental mutations.
 */
export const processConfiguration = (
  eConf: Partial<ConfigurationType>,
  language: (typeof supportedLanguages)[number],
): ConfigurationType => {
  const icons = Object.entries(eConf.icons || {}).reduce(
    (acc, [key, value]) => {
      if (typeof value?.d === 'string') {
        acc[key] = value;
      } else {
        logger.error(`icons.${key} contains invalid color key structure`, eConf.icons);
      }

      return acc;
    },
    {} as Record<string, ConfigurationIconType>,
  );

  const colorPresets = Object.entries(eConf.colorPresets || {}).reduce(
    (acc, [key, value]) => {
      if (
        value &&
        colord(value.dark).isValid() &&
        colord(value.main).isValid() &&
        colord(value.light).isValid() &&
        colord(value.contrastText).isValid()
      ) {
        acc[`${CUSTOM_CONFIGURATION_OPTION_PREFIX}${key}` as string] = value as PaletteColor;
      } else {
        logger.error(`colorPresets.${key} contains invalid color key structure`, eConf.colorPresets);
      }

      return acc;
    },
    {} as Record<string, PaletteColor>,
  );

  const colors = Object.entries(eConf.colors || {}).reduce(
    (acc, [key, value]) => {
      if (colord(value).isValid()) {
        acc[key] = value;
      } else {
        logger.error(`colors.${key} contains invalid color`, eConf.colors);
      }

      return acc;
    },
    {} as Record<string, string>,
  );

  const typographies = Object.entries(eConf.typographies || {}).reduce(
    (acc, [key, value]) => {
      if (typeof value.fontSize === 'number') {
        acc[`${CUSTOM_CONFIGURATION_OPTION_PREFIX}${key}`] = value;
      } else {
        logger.error(`typographies.${key} contains invalid key`, eConf.typographies);
      }

      return acc;
    },
    {} as Record<string, TypographyStyle>,
  );

  const string = (value?: string) => {
    if (typeof value === 'string') {
      return value;
    }

    return undefined;
  };

  const number = (value?: number) => {
    if (typeof value === 'number') {
      return value;
    }

    return undefined;
  };

  const boolean = (value?: boolean) => {
    if (typeof value === 'boolean') {
      return value;
    }

    return undefined;
  };

  const variantValues = [VARIANT_TEXT, VARIANT_OUTLINED, VARIANT_FILLED];

  const variant = (value?: (typeof variantValues)[number]) => {
    if (typeof value === 'string') {
      if (variantValues.includes(value)) {
        return value;
      }

      logger.error(`Invalid variant value: ${value}`, variantValues);
    }

    return undefined;
  };

  const variantInputValues = [VARIANT_OUTLINED, VARIANT_FILLED];

  const inputVariant = (value?: (typeof variantInputValues)[number]) => {
    if (typeof value === 'string') {
      if (variantInputValues.includes(value)) {
        return value;
      }

      logger.error(`Invalid input variant value: ${value}`, variantInputValues);
    }

    return undefined;
  };

  const sizeTabVariant = [SIZE_SMALL, SIZE_MEDIUM];

  const tabSize = (value?: (typeof sizeTabVariant)[number]) => {
    if (typeof value === 'string') {
      if (sizeTabVariant.includes(value)) {
        return value;
      }

      logger.error(`Invalid tab size value: ${value}`, sizeTabVariant);
    }

    return undefined;
  };

  const sizeVariant = [SIZE_X_SMALL, SIZE_SMALL, SIZE_MEDIUM, SIZE_LARGE];

  const size = (value?: (typeof sizeVariant)[number]) => {
    if (typeof value === 'string') {
      if (sizeVariant.includes(value)) {
        return value;
      }

      logger.error(`Invalid size value: ${value}`, sizeVariant);
    }

    return undefined;
  };

  const sizeSearchInputVariant = [SIZE_X_SMALL, SIZE_SMALL, SIZE_MEDIUM, SIZE_LARGE, SIZE_X_LARGE];

  const inputSearchSize = (value?: (typeof sizeSearchInputVariant)[number]) => {
    if (typeof value === 'string') {
      if (sizeSearchInputVariant.includes(value)) {
        return value;
      }

      logger.error(`Invalid Input Search size value: ${value}`, sizeSearchInputVariant);
    }

    return undefined;
  };

  const svgSize = (value?: (typeof configurationSvgSizes)[number]) => {
    if (typeof value === 'string') {
      if (configurationSvgSizes.includes(value)) {
        return value;
      }

      logger.error(`Invalid SVG size value: ${value}`, configurationSvgSizes);
    }

    return undefined;
  };

  const opacity = (value?: number) => {
    if (typeof value === 'number') {
      if (value >= 0 && value <= 1) {
        return value;
      }

      logger.error(`Invalid opacity value: ${value}`);
    }

    return undefined;
  };

  const shapeVariants = [SHAPE_SQUARE, SHAPE_ROUNDED, SHAPE_CIRCULAR];

  const shape = (value?: (typeof shapeVariants)[number]) => {
    if (typeof value === 'string') {
      if (shapeVariants.includes(value)) {
        return value;
      }

      logger.error(`Invalid shape value: ${value}`);
    }

    return undefined;
  };

  const icon = (iconKey?: string) => {
    if (typeof iconKey === 'string') {
      if (Object.prototype.hasOwnProperty.call(icons, iconKey)) {
        return iconKey;
      }

      logger.error(`icons.${iconKey} key doesn't exist in:`, icons);
    }

    return undefined;
  };

  const typography = (typographyKey?: string) => {
    if (typeof typographyKey === 'string') {
      const targetTKey = `${CUSTOM_CONFIGURATION_OPTION_PREFIX}${typographyKey}`;

      if (Object.prototype.hasOwnProperty.call(typographies, targetTKey)) {
        return targetTKey;
      }

      logger.error(`typographies.${typographyKey} key doesn't exist in:`, eConf.typographies);
    }

    return undefined;
  };

  const colorGroup = (color?: string) => {
    if (typeof color === 'string') {
      const paletteColorKey = `${CUSTOM_CONFIGURATION_OPTION_PREFIX}${color}`;
      const group = colorPresets[paletteColorKey];

      if (group && group.dark && group.main && group.light && group.contrastText) {
        return paletteColorKey as ButtonOwnProps['color'];
      }

      logger.error(`colorPresets key ${color} doesn't exist in:`, eConf.colorPresets);
    }

    return undefined;
  };

  const singleColor = (color?: string) => {
    if (typeof color === 'string') {
      const [paletteColorKey, colorName] = `${CUSTOM_CONFIGURATION_OPTION_PREFIX}${color}`.split('.');

      if (color.startsWith('colors.')) {
        if (colorName && typeof colors[colorName] === 'string') {
          return `${paletteColorKey}.${colorName}` as ButtonOwnProps['color'];
        }

        logger.error(`Color key ${color} doesn't exist in:`, colors);
      } else if (typeof colorPresets[paletteColorKey]?.[colorName as keyof PaletteColor] === 'string') {
        return `${paletteColorKey}.${colorName}` as ButtonOwnProps['color'];
      }

      logger.error(`Color key ${color} doesn't exist in:`, colorPresets);
    }

    return undefined;
  };

  const fontWeightList = [100, 200, 300, 400, 500, 600, 700, 800, 900] as const;

  const fontWeight = (value?: (typeof fontWeightList)[number]): (typeof fontWeightList)[number] | undefined => {
    if (typeof value === 'number') {
      if (fontWeightList.includes(value)) {
        return value;
      }

      logger.error(`Invalid fontWeight key ${value}`, fontWeightList);
    }

    return undefined;
  };

  const result: ConfigurationType = {
    icons,
    colors,
    colorPresets,
    typographies,
    common: {
      dateTimeFormat:
        eConf.common?.dateTimeFormat && typeof eConf.common.dateTimeFormat === 'string'
          ? eConf.common.dateTimeFormat
          : defaultDateTimeFormat[language],
      offsetTop: number(eConf.common?.offsetTop),
      offsetBottom: number(eConf.common?.offsetBottom),
      offsetTopMobile: number(eConf.common?.offsetTopMobile),
      offsetBottomMobile: number(eConf.common?.offsetBottomMobile),
      search: {
        input: {
          fontFamily: string(eConf.common?.search?.input?.fontFamily),
          placeholderText: string(eConf.common?.search?.input?.placeholderText),
          shape: shape(eConf.common?.search?.input?.shape),
          size: inputSearchSize(eConf.common?.search?.input?.size),
          variant: inputVariant(eConf.common?.search?.input?.variant),
          borderColor: singleColor(eConf.common?.search?.input?.borderColor),
          borderColorHover: singleColor(eConf.common?.search?.input?.borderColorHover),
          borderColorFocus: singleColor(eConf.common?.search?.input?.borderColorFocus),
        },
        searchButton: {
          icon: icon(eConf.common?.search?.searchButton?.icon),
          variant: variant(eConf.common?.search?.searchButton?.variant),
          color: colorGroup(eConf.common?.search?.searchButton?.color) as ButtonOwnProps['color'],
        },
        searchLocalButton: {
          icon: icon(eConf.common?.search?.searchLocalButton?.icon),
          variant: variant(eConf.common?.search?.searchLocalButton?.variant),
          color: colorGroup(eConf.common?.search?.searchLocalButton?.color) as ButtonOwnProps['color'],
        },
        searchGlobalButton: {
          icon: icon(eConf.common?.search?.searchGlobalButton?.icon),
          variant: variant(eConf.common?.search?.searchGlobalButton?.variant),
          color: colorGroup(eConf.common?.search?.searchGlobalButton?.color) as ButtonOwnProps['color'],
        },
        filters: {
          visible: boolean(eConf.common?.search?.filters?.visible),
          size: size(eConf.common?.search?.filters?.size),
          variant: variant(eConf.common?.search?.filters?.variant),
          shape: shape(eConf.common?.search?.filters?.shape),
          color: colorGroup(eConf.common?.search?.filters?.color) as ButtonOwnProps['color'],
          controllers: {
            language: {
              visible: boolean(eConf.common?.search?.filters?.controllers?.language?.visible),
            },
            time: {
              visible: boolean(eConf.common?.search?.filters?.controllers?.time?.visible),
            },
            duration: {
              visible: boolean(eConf.common?.search?.filters?.controllers?.duration?.visible),
            },
          },
        },
      },
      chips: {
        fontFamily: string(eConf.common?.chips?.fontFamily),
        size: size(eConf.common?.chips?.size),
        color: colorGroup(eConf.common?.chips?.color) as ChipOwnProps['color'],
        variant: variant(eConf.common?.chips?.variant),
        selectedColor: colorGroup(eConf.common?.chips?.selectedColor) as ChipOwnProps['color'],
        selectedVariant: variant(eConf.common?.chips?.selectedVariant),
      },
      videoActions: {
        visible: boolean(eConf.common?.videoActions?.visible),
        size: svgSize(eConf.common?.videoActions?.size),
        color: singleColor(eConf.common?.videoActions?.color),
        edit: {
          icon: icon(eConf.common?.videoActions?.edit?.icon),
          visible: boolean(eConf.common?.videoActions?.edit?.visible),
        },
        download: {
          icon: icon(eConf.common?.videoActions?.download?.icon),
          visible: boolean(eConf.common?.videoActions?.download?.visible),
        },
        share: {
          icon: icon(eConf.common?.videoActions?.share?.icon),
          visible: boolean(eConf.common?.videoActions?.share?.visible),
        },
        favorites: {
          icon: icon(eConf.common?.videoActions?.favorites?.icon),
          iconInactive: icon(eConf.common?.videoActions?.favorites?.iconInactive),
          visible: boolean(eConf.common?.videoActions?.favorites?.visible),
        },
      },
      socialButtons: {
        size: size(eConf.common?.socialButtons?.size),
        facebook: {
          icon: icon(eConf.common?.socialButtons?.facebook?.icon),
          color: singleColor(eConf.common?.socialButtons?.facebook?.color),
          visible: boolean(eConf.common?.socialButtons?.facebook?.visible),
        },
        twitter: {
          icon: icon(eConf.common?.socialButtons?.twitter?.icon),
          color: singleColor(eConf.common?.socialButtons?.twitter?.color),
          visible: boolean(eConf.common?.socialButtons?.twitter?.visible),
        },
        linkedin: {
          icon: icon(eConf.common?.socialButtons?.linkedin?.icon),
          color: singleColor(eConf.common?.socialButtons?.linkedin?.color),
          visible: boolean(eConf.common?.socialButtons?.linkedin?.visible),
        },
        whatsapp: {
          icon: icon(eConf.common?.socialButtons?.whatsapp?.icon),
          color: singleColor(eConf.common?.socialButtons?.whatsapp?.color),
          visible: boolean(eConf.common?.socialButtons?.whatsapp?.visible),
        },
        email: {
          icon: icon(eConf.common?.socialButtons?.email?.icon),
          color: singleColor(eConf.common?.socialButtons?.email?.color),
          visible: boolean(eConf.common?.socialButtons?.email?.visible),
        },
      },
      videoEngagement: {
        [VIEW_KEY]: {
          icon: icon(eConf.common?.videoEngagement?.[VIEW_KEY]?.icon),
          iconInactive: icon(eConf.common?.videoEngagement?.[VIEW_KEY]?.iconInactive),
          fontFamily: string(eConf.common?.videoEngagement?.[VIEW_KEY]?.fontFamily),
          fontWeight: fontWeight(eConf.common?.videoEngagement?.[VIEW_KEY]?.fontWeight),
          typography: typography(eConf.common?.videoEngagement?.[VIEW_KEY]?.typography),
          color: singleColor(eConf.common?.videoEngagement?.[VIEW_KEY]?.color),
          colorInactive: singleColor(eConf.common?.videoEngagement?.[VIEW_KEY]?.colorInactive),
          visible: boolean(eConf.common?.videoEngagement?.[VIEW_KEY]?.visible),
        },
        [LIKE_KEY]: {
          icon: icon(eConf.common?.videoEngagement?.[LIKE_KEY]?.icon),
          iconInactive: icon(eConf.common?.videoEngagement?.[LIKE_KEY]?.iconInactive),
          fontFamily: string(eConf.common?.videoEngagement?.[LIKE_KEY]?.fontFamily),
          fontWeight: fontWeight(eConf.common?.videoEngagement?.[LIKE_KEY]?.fontWeight),
          typography: typography(eConf.common?.videoEngagement?.[LIKE_KEY]?.typography),
          color: singleColor(eConf.common?.videoEngagement?.[LIKE_KEY]?.color),
          colorInactive: singleColor(eConf.common?.videoEngagement?.[LIKE_KEY]?.colorInactive),
          visible: boolean(eConf.common?.videoEngagement?.[LIKE_KEY]?.visible),
        },
        [SHARE_KEY]: {
          icon: icon(eConf.common?.videoEngagement?.[SHARE_KEY]?.icon),
          iconInactive: icon(eConf.common?.videoEngagement?.[SHARE_KEY]?.iconInactive),
          fontFamily: string(eConf.common?.videoEngagement?.[SHARE_KEY]?.fontFamily),
          fontWeight: fontWeight(eConf.common?.videoEngagement?.[SHARE_KEY]?.fontWeight),
          typography: typography(eConf.common?.videoEngagement?.[SHARE_KEY]?.typography),
          color: singleColor(eConf.common?.videoEngagement?.[SHARE_KEY]?.color),
          colorInactive: singleColor(eConf.common?.videoEngagement?.[SHARE_KEY]?.colorInactive),
          visible: boolean(eConf.common?.videoEngagement?.[SHARE_KEY]?.visible),
        },
      },
    },
    modules: {
      channelList: {
        channels: {
          byType: {},
          byId: {},
        },
      },
      playerOverview: {
        tabs: {
          fontFamily: string(eConf.modules?.playerOverview?.tabs?.fontFamily),
          size: tabSize(eConf.modules?.playerOverview?.tabs?.size),
          color: colorGroup(eConf.modules?.playerOverview?.tabs?.color),
          indicatorColor: colorGroup(eConf.modules?.playerOverview?.tabs?.indicatorColor),
        },
        deepSearchTabs: {
          fontFamily: string(eConf.modules?.playerOverview?.deepSearchTabs?.fontFamily),
          size: tabSize(eConf.modules?.playerOverview?.deepSearchTabs?.size),
          color: colorGroup(eConf.modules?.playerOverview?.deepSearchTabs?.color),
          indicatorColor: colorGroup(eConf.modules?.playerOverview?.deepSearchTabs?.indicatorColor),
        },
        videoCard: {
          activeColor: singleColor(eConf.modules?.playerOverview?.videoCard?.activeColor),
          overlay: {
            backgroundColor: singleColor(eConf.modules?.playerOverview?.videoCard?.overlay?.backgroundColor),
            backgroundOpacity: opacity(eConf.modules?.playerOverview?.videoCard?.overlay?.backgroundOpacity),
            visible: boolean(eConf.modules?.playerOverview?.videoCard?.overlay?.visible),
          },
          title: {
            fontFamily: string(eConf.modules?.playerOverview?.videoCard?.title?.fontFamily),
            fontWeight: fontWeight(eConf.modules?.playerOverview?.videoCard?.title?.fontWeight),
            typography: typography(eConf.modules?.playerOverview?.videoCard?.title?.typography),
            color: singleColor(eConf.modules?.playerOverview?.videoCard?.title?.color),
            visible: boolean(eConf.modules?.playerOverview?.videoCard?.title?.visible),
          },
          description: {
            fontFamily: string(eConf.modules?.playerOverview?.videoCard?.description?.fontFamily),
            fontWeight: fontWeight(eConf.modules?.playerOverview?.videoCard?.description?.fontWeight),
            typography: typography(eConf.modules?.playerOverview?.videoCard?.description?.typography),
            color: singleColor(eConf.modules?.playerOverview?.videoCard?.description?.color),
            visible: boolean(eConf.modules?.playerOverview?.videoCard?.description?.visible),
          },
          dateTime: {
            fontFamily: string(eConf.modules?.playerOverview?.videoCard?.dateTime?.fontFamily),
            fontWeight: fontWeight(eConf.modules?.playerOverview?.videoCard?.dateTime?.fontWeight),
            typography: typography(eConf.modules?.playerOverview?.videoCard?.dateTime?.typography),
            color: singleColor(eConf.modules?.playerOverview?.videoCard?.dateTime?.color),
            visible: boolean(eConf.modules?.playerOverview?.videoCard?.dateTime?.visible),
          },
          duration: {
            fontFamily: string(eConf.modules?.playerOverview?.videoCard?.duration?.fontFamily),
            fontWeight: fontWeight(eConf.modules?.playerOverview?.videoCard?.duration?.fontWeight),
            typography: typography(eConf.modules?.playerOverview?.videoCard?.duration?.typography),
            color: singleColor(eConf.modules?.playerOverview?.videoCard?.duration?.color),
            backgroundColor: singleColor(eConf.modules?.playerOverview?.videoCard?.duration?.backgroundColor),
            visible: boolean(eConf.modules?.playerOverview?.videoCard?.duration?.visible),
          },
          videoEngagement: {
            [VIEW_KEY]: {
              icon: icon(eConf.modules?.playerOverview?.videoCard?.videoEngagement?.[VIEW_KEY]?.icon),
              iconInactive: icon(eConf.modules?.playerOverview?.videoCard?.videoEngagement?.[VIEW_KEY]?.iconInactive),
              fontFamily: string(eConf.modules?.playerOverview?.videoCard?.videoEngagement?.[VIEW_KEY]?.fontFamily),
              fontWeight: fontWeight(eConf.modules?.playerOverview?.videoCard?.videoEngagement?.[VIEW_KEY]?.fontWeight),
              typography: typography(eConf.modules?.playerOverview?.videoCard?.videoEngagement?.[VIEW_KEY]?.typography),
              color: singleColor(eConf.modules?.playerOverview?.videoCard?.videoEngagement?.[VIEW_KEY]?.color),
              colorInactive: singleColor(
                eConf.modules?.playerOverview?.videoCard?.videoEngagement?.[VIEW_KEY]?.colorInactive,
              ),
              visible: boolean(eConf.modules?.playerOverview?.videoCard?.videoEngagement?.[VIEW_KEY]?.visible),
            },
            [LIKE_KEY]: {
              icon: icon(eConf.modules?.playerOverview?.videoCard?.videoEngagement?.[LIKE_KEY]?.icon),
              iconInactive: icon(eConf.modules?.playerOverview?.videoCard?.videoEngagement?.[LIKE_KEY]?.iconInactive),
              fontFamily: string(eConf.modules?.playerOverview?.videoCard?.videoEngagement?.[LIKE_KEY]?.fontFamily),
              fontWeight: fontWeight(eConf.modules?.playerOverview?.videoCard?.videoEngagement?.[LIKE_KEY]?.fontWeight),
              typography: typography(eConf.modules?.playerOverview?.videoCard?.videoEngagement?.[LIKE_KEY]?.typography),
              color: singleColor(eConf.modules?.playerOverview?.videoCard?.videoEngagement?.[LIKE_KEY]?.color),
              colorInactive: singleColor(
                eConf.modules?.playerOverview?.videoCard?.videoEngagement?.[LIKE_KEY]?.colorInactive,
              ),
              visible: boolean(eConf.modules?.playerOverview?.videoCard?.videoEngagement?.[LIKE_KEY]?.visible),
            },
            [SHARE_KEY]: {
              icon: icon(eConf.modules?.playerOverview?.videoCard?.videoEngagement?.[SHARE_KEY]?.icon),
              iconInactive: icon(eConf.modules?.playerOverview?.videoCard?.videoEngagement?.[SHARE_KEY]?.iconInactive),
              fontFamily: string(eConf.modules?.playerOverview?.videoCard?.videoEngagement?.[SHARE_KEY]?.fontFamily),
              fontWeight: fontWeight(
                eConf.modules?.playerOverview?.videoCard?.videoEngagement?.[SHARE_KEY]?.fontWeight,
              ),
              typography: typography(
                eConf.modules?.playerOverview?.videoCard?.videoEngagement?.[SHARE_KEY]?.typography,
              ),
              color: singleColor(eConf.modules?.playerOverview?.videoCard?.videoEngagement?.[SHARE_KEY]?.color),
              colorInactive: singleColor(
                eConf.modules?.playerOverview?.videoCard?.videoEngagement?.[SHARE_KEY]?.colorInactive,
              ),
              visible: boolean(eConf.modules?.playerOverview?.videoCard?.videoEngagement?.[SHARE_KEY]?.visible),
            },
          },
        },
        tabsPanel: {
          playlist: {
            activeColor: singleColor(eConf.modules?.playerOverview?.tabsPanel?.playlist?.activeColor),
            title: {
              fontFamily: string(eConf.modules?.playerOverview?.tabsPanel?.playlist?.title?.fontFamily),
              fontWeight: fontWeight(eConf.modules?.playerOverview?.tabsPanel?.playlist?.title?.fontWeight),
              typography: typography(eConf.modules?.playerOverview?.tabsPanel?.playlist?.title?.typography),
              color: singleColor(eConf.modules?.playerOverview?.tabsPanel?.playlist?.title?.color),
              visible: boolean(eConf.modules?.playerOverview?.tabsPanel?.playlist?.title?.visible),
            },
            description: {
              fontFamily: string(eConf.modules?.playerOverview?.tabsPanel?.playlist?.description?.fontFamily),
              fontWeight: fontWeight(eConf.modules?.playerOverview?.tabsPanel?.playlist?.description?.fontWeight),
              typography: typography(eConf.modules?.playerOverview?.tabsPanel?.playlist?.description?.typography),
              color: singleColor(eConf.modules?.playerOverview?.tabsPanel?.playlist?.description?.color),
              visible: boolean(eConf.modules?.playerOverview?.tabsPanel?.playlist?.description?.visible),
            },
            dateTime: {
              fontFamily: string(eConf.modules?.playerOverview?.tabsPanel?.playlist?.dateTime?.fontFamily),
              fontWeight: fontWeight(eConf.modules?.playerOverview?.tabsPanel?.playlist?.dateTime?.fontWeight),
              typography: typography(eConf.modules?.playerOverview?.tabsPanel?.playlist?.dateTime?.typography),
              color: singleColor(eConf.modules?.playerOverview?.tabsPanel?.playlist?.dateTime?.color),
              visible: boolean(eConf.modules?.playerOverview?.tabsPanel?.playlist?.dateTime?.visible),
            },
            duration: {
              fontFamily: string(eConf.modules?.playerOverview?.tabsPanel?.playlist?.duration?.fontFamily),
              fontWeight: fontWeight(eConf.modules?.playerOverview?.tabsPanel?.playlist?.duration?.fontWeight),
              typography: typography(eConf.modules?.playerOverview?.tabsPanel?.playlist?.duration?.typography),
              color: singleColor(eConf.modules?.playerOverview?.tabsPanel?.playlist?.duration?.color),
              backgroundColor: singleColor(
                eConf.modules?.playerOverview?.tabsPanel?.playlist?.duration?.backgroundColor,
              ),
              visible: boolean(eConf.modules?.playerOverview?.tabsPanel?.playlist?.duration?.visible),
            },
            videoEngagement: {
              [VIEW_KEY]: {
                icon: icon(eConf.modules?.playerOverview?.tabsPanel?.playlist?.videoEngagement?.[VIEW_KEY]?.icon),
                iconInactive: icon(
                  eConf.modules?.playerOverview?.tabsPanel?.playlist?.videoEngagement?.[VIEW_KEY]?.iconInactive,
                ),
                fontFamily: string(
                  eConf.modules?.playerOverview?.tabsPanel?.playlist?.videoEngagement?.[VIEW_KEY]?.fontFamily,
                ),
                fontWeight: fontWeight(
                  eConf.modules?.playerOverview?.tabsPanel?.playlist?.videoEngagement?.[VIEW_KEY]?.fontWeight,
                ),
                typography: typography(
                  eConf.modules?.playerOverview?.tabsPanel?.playlist?.videoEngagement?.[VIEW_KEY]?.typography,
                ),
                color: singleColor(
                  eConf.modules?.playerOverview?.tabsPanel?.playlist?.videoEngagement?.[VIEW_KEY]?.color,
                ),
                colorInactive: singleColor(
                  eConf.modules?.playerOverview?.tabsPanel?.playlist?.videoEngagement?.[VIEW_KEY]?.colorInactive,
                ),
                visible: boolean(
                  eConf.modules?.playerOverview?.tabsPanel?.playlist?.videoEngagement?.[VIEW_KEY]?.visible,
                ),
              },
              [LIKE_KEY]: {
                icon: icon(eConf.modules?.playerOverview?.tabsPanel?.playlist?.videoEngagement?.[LIKE_KEY]?.icon),
                iconInactive: icon(
                  eConf.modules?.playerOverview?.tabsPanel?.playlist?.videoEngagement?.[LIKE_KEY]?.iconInactive,
                ),
                fontFamily: string(
                  eConf.modules?.playerOverview?.tabsPanel?.playlist?.videoEngagement?.[LIKE_KEY]?.fontFamily,
                ),
                fontWeight: fontWeight(
                  eConf.modules?.playerOverview?.tabsPanel?.playlist?.videoEngagement?.[LIKE_KEY]?.fontWeight,
                ),
                typography: typography(
                  eConf.modules?.playerOverview?.tabsPanel?.playlist?.videoEngagement?.[LIKE_KEY]?.typography,
                ),
                color: singleColor(
                  eConf.modules?.playerOverview?.tabsPanel?.playlist?.videoEngagement?.[LIKE_KEY]?.color,
                ),
                colorInactive: singleColor(
                  eConf.modules?.playerOverview?.tabsPanel?.playlist?.videoEngagement?.[LIKE_KEY]?.colorInactive,
                ),
                visible: boolean(
                  eConf.modules?.playerOverview?.tabsPanel?.playlist?.videoEngagement?.[LIKE_KEY]?.visible,
                ),
              },
              [SHARE_KEY]: {
                icon: icon(eConf.modules?.playerOverview?.tabsPanel?.playlist?.videoEngagement?.[SHARE_KEY]?.icon),
                iconInactive: icon(
                  eConf.modules?.playerOverview?.tabsPanel?.playlist?.videoEngagement?.[SHARE_KEY]?.iconInactive,
                ),
                fontFamily: string(
                  eConf.modules?.playerOverview?.tabsPanel?.playlist?.videoEngagement?.[SHARE_KEY]?.fontFamily,
                ),
                fontWeight: fontWeight(
                  eConf.modules?.playerOverview?.tabsPanel?.playlist?.videoEngagement?.[SHARE_KEY]?.fontWeight,
                ),
                typography: typography(
                  eConf.modules?.playerOverview?.tabsPanel?.playlist?.videoEngagement?.[SHARE_KEY]?.typography,
                ),
                color: singleColor(
                  eConf.modules?.playerOverview?.tabsPanel?.playlist?.videoEngagement?.[SHARE_KEY]?.color,
                ),
                colorInactive: singleColor(
                  eConf.modules?.playerOverview?.tabsPanel?.playlist?.videoEngagement?.[SHARE_KEY]?.colorInactive,
                ),
                visible: boolean(
                  eConf.modules?.playerOverview?.tabsPanel?.playlist?.videoEngagement?.[SHARE_KEY]?.visible,
                ),
              },
            },
          },
        },
      },
      search: {
        resultTitle: {
          fontFamily: string(eConf.modules?.search?.resultTitle?.fontFamily),
          fontWeight: fontWeight(eConf.modules?.search?.resultTitle?.fontWeight),
          typography: typography(eConf.modules?.search?.resultTitle?.typography),
          color: singleColor(eConf.modules?.search?.resultTitle?.color),
          visible: boolean(eConf.modules?.search?.resultTitle?.visible),
        },
        videoCard: {
          title: {
            fontFamily: string(eConf.modules?.search?.videoCard?.title?.fontFamily),
            fontWeight: fontWeight(eConf.modules?.search?.videoCard?.title?.fontWeight),
            typography: typography(eConf.modules?.search?.videoCard?.title?.typography),
            color: singleColor(eConf.modules?.search?.videoCard?.title?.color),
            visible: boolean(eConf.modules?.search?.videoCard?.title?.visible),
          },
          description: {
            fontFamily: string(eConf.modules?.search?.videoCard?.description?.fontFamily),
            fontWeight: fontWeight(eConf.modules?.search?.videoCard?.description?.fontWeight),
            typography: typography(eConf.modules?.search?.videoCard?.description?.typography),
            color: singleColor(eConf.modules?.search?.videoCard?.description?.color),
            visible: boolean(eConf.modules?.search?.videoCard?.description?.visible),
          },
          dateTime: {
            fontFamily: string(eConf.modules?.search?.videoCard?.dateTime?.fontFamily),
            fontWeight: fontWeight(eConf.modules?.search?.videoCard?.dateTime?.fontWeight),
            typography: typography(eConf.modules?.search?.videoCard?.dateTime?.typography),
            color: singleColor(eConf.modules?.search?.videoCard?.dateTime?.color),
            visible: boolean(eConf.modules?.search?.videoCard?.dateTime?.visible),
          },
          duration: {
            fontFamily: string(eConf.modules?.search?.videoCard?.duration?.fontFamily),
            fontWeight: fontWeight(eConf.modules?.search?.videoCard?.duration?.fontWeight),
            typography: typography(eConf.modules?.search?.videoCard?.duration?.typography),
            color: singleColor(eConf.modules?.search?.videoCard?.duration?.color),
            backgroundColor: singleColor(eConf.modules?.search?.videoCard?.duration?.backgroundColor),
            visible: boolean(eConf.modules?.search?.videoCard?.duration?.visible),
          },
          videoEngagement: {
            [VIEW_KEY]: {
              icon: icon(eConf.modules?.search?.videoCard?.videoEngagement?.[VIEW_KEY]?.icon),
              iconInactive: icon(eConf.modules?.search?.videoCard?.videoEngagement?.[VIEW_KEY]?.iconInactive),
              fontFamily: string(eConf.modules?.search?.videoCard?.videoEngagement?.[VIEW_KEY]?.fontFamily),
              fontWeight: fontWeight(eConf.modules?.search?.videoCard?.videoEngagement?.[VIEW_KEY]?.fontWeight),
              typography: typography(eConf.modules?.search?.videoCard?.videoEngagement?.[VIEW_KEY]?.typography),
              color: singleColor(eConf.modules?.search?.videoCard?.videoEngagement?.[VIEW_KEY]?.color),
              colorInactive: singleColor(eConf.modules?.search?.videoCard?.videoEngagement?.[VIEW_KEY]?.colorInactive),
              visible: boolean(eConf.modules?.search?.videoCard?.videoEngagement?.[VIEW_KEY]?.visible),
            },
            [LIKE_KEY]: {
              icon: icon(eConf.modules?.search?.videoCard?.videoEngagement?.[LIKE_KEY]?.icon),
              iconInactive: icon(eConf.modules?.search?.videoCard?.videoEngagement?.[LIKE_KEY]?.iconInactive),
              fontFamily: string(eConf.modules?.search?.videoCard?.videoEngagement?.[LIKE_KEY]?.fontFamily),
              fontWeight: fontWeight(eConf.modules?.search?.videoCard?.videoEngagement?.[LIKE_KEY]?.fontWeight),
              typography: typography(eConf.modules?.search?.videoCard?.videoEngagement?.[LIKE_KEY]?.typography),
              color: singleColor(eConf.modules?.search?.videoCard?.videoEngagement?.[LIKE_KEY]?.color),
              colorInactive: singleColor(eConf.modules?.search?.videoCard?.videoEngagement?.[LIKE_KEY]?.colorInactive),
              visible: boolean(eConf.modules?.search?.videoCard?.videoEngagement?.[LIKE_KEY]?.visible),
            },
            [SHARE_KEY]: {
              icon: icon(eConf.modules?.search?.videoCard?.videoEngagement?.[SHARE_KEY]?.icon),
              iconInactive: icon(eConf.modules?.search?.videoCard?.videoEngagement?.[SHARE_KEY]?.iconInactive),
              fontFamily: string(eConf.modules?.search?.videoCard?.videoEngagement?.[SHARE_KEY]?.fontFamily),
              fontWeight: fontWeight(eConf.modules?.search?.videoCard?.videoEngagement?.[SHARE_KEY]?.fontWeight),
              typography: typography(eConf.modules?.search?.videoCard?.videoEngagement?.[SHARE_KEY]?.typography),
              color: singleColor(eConf.modules?.search?.videoCard?.videoEngagement?.[SHARE_KEY]?.color),
              colorInactive: singleColor(eConf.modules?.search?.videoCard?.videoEngagement?.[SHARE_KEY]?.colorInactive),
              visible: boolean(eConf.modules?.search?.videoCard?.videoEngagement?.[SHARE_KEY]?.visible),
            },
          },
        },
        getAI: {
          videoEngagement: {
            [VIEW_KEY]: {
              icon: icon(eConf.modules?.search?.getAI?.videoEngagement[VIEW_KEY]?.icon),
              iconInactive: icon(eConf.modules?.search?.getAI?.videoEngagement[VIEW_KEY]?.iconInactive),
              fontFamily: string(eConf.modules?.search?.getAI?.videoEngagement[VIEW_KEY]?.fontFamily),
              fontWeight: fontWeight(eConf.modules?.search?.getAI?.videoEngagement[VIEW_KEY]?.fontWeight),
              typography: typography(eConf.modules?.search?.getAI?.videoEngagement[VIEW_KEY]?.typography),
              color: singleColor(eConf.modules?.search?.getAI?.videoEngagement[VIEW_KEY]?.color),
              colorInactive: singleColor(eConf.modules?.search?.getAI?.videoEngagement[VIEW_KEY]?.colorInactive),
              visible: boolean(eConf.modules?.search?.getAI?.videoEngagement[VIEW_KEY]?.visible),
            },
            [LIKE_KEY]: {
              icon: icon(eConf.modules?.search?.getAI?.videoEngagement[LIKE_KEY]?.icon),
              iconInactive: icon(eConf.modules?.search?.getAI?.videoEngagement[LIKE_KEY]?.iconInactive),
              fontFamily: string(eConf.modules?.search?.getAI?.videoEngagement[LIKE_KEY]?.fontFamily),
              fontWeight: fontWeight(eConf.modules?.search?.getAI?.videoEngagement[LIKE_KEY]?.fontWeight),
              typography: typography(eConf.modules?.search?.getAI?.videoEngagement[LIKE_KEY]?.typography),
              color: singleColor(eConf.modules?.search?.getAI?.videoEngagement[LIKE_KEY]?.color),
              colorInactive: singleColor(eConf.modules?.search?.getAI?.videoEngagement[LIKE_KEY]?.colorInactive),
              visible: boolean(eConf.modules?.search?.getAI?.videoEngagement[LIKE_KEY]?.visible),
            },
            [SHARE_KEY]: {
              icon: icon(eConf.modules?.search?.getAI?.videoEngagement[SHARE_KEY]?.icon),
              iconInactive: icon(eConf.modules?.search?.getAI?.videoEngagement[SHARE_KEY]?.iconInactive),
              fontFamily: string(eConf.modules?.search?.getAI?.videoEngagement[SHARE_KEY]?.fontFamily),
              fontWeight: fontWeight(eConf.modules?.search?.getAI?.videoEngagement[SHARE_KEY]?.fontWeight),
              typography: typography(eConf.modules?.search?.getAI?.videoEngagement[SHARE_KEY]?.typography),
              color: singleColor(eConf.modules?.search?.getAI?.videoEngagement[SHARE_KEY]?.color),
              colorInactive: singleColor(eConf.modules?.search?.getAI?.videoEngagement[SHARE_KEY]?.colorInactive),
              visible: boolean(eConf.modules?.search?.getAI?.videoEngagement[SHARE_KEY]?.visible),
            },
          },
        },
      },
    },
  };

  const fillChannel = (eChannel: Partial<ChannelConfig>): ChannelConfig => ({
    title: {
      fontFamily: string(eChannel?.title?.fontFamily),
      fontWeight: fontWeight(eChannel?.title?.fontWeight),
      typography: typography(eChannel?.title?.typography),
      color: singleColor(eChannel?.title?.color),
      visible: boolean(eChannel?.title?.visible),
    },
    description: {
      fontFamily: string(eChannel?.description?.fontFamily),
      fontWeight: fontWeight(eChannel?.description?.fontWeight),
      typography: typography(eChannel?.description?.typography),
      color: singleColor(eChannel?.description?.color),
      visible: boolean(eChannel?.description?.visible),
    },
    viewAll: {
      fontFamily: string(eChannel?.viewAll?.fontFamily),
      fontWeight: fontWeight(eChannel?.viewAll?.fontWeight),
      typography: typography(eChannel?.viewAll?.typography),
      color: singleColor(eChannel?.viewAll?.color),
      visible: boolean(eChannel?.viewAll?.visible),
    },
    videoCard: {
      visible: boolean(eChannel?.videoCard?.visible),
      static: boolean(eChannel?.videoCard?.static),
      activeColor: singleColor(eChannel?.videoCard?.activeColor),
      overlay: {
        backgroundColor: singleColor(eChannel?.videoCard?.overlay?.backgroundColor),
        backgroundOpacity: opacity(eChannel?.videoCard?.overlay?.backgroundOpacity),
        visible: boolean(eChannel?.videoCard?.overlay?.visible),
      },
      title: {
        fontFamily: string(eChannel?.videoCard?.title?.fontFamily),
        fontWeight: fontWeight(eChannel?.videoCard?.title?.fontWeight),
        typography: typography(eChannel?.videoCard?.title?.typography),
        color: singleColor(eChannel?.videoCard?.title?.color),
        visible: boolean(eChannel?.videoCard?.title?.visible),
      },
      description: {
        fontFamily: string(eChannel?.videoCard?.description?.fontFamily),
        fontWeight: fontWeight(eChannel?.videoCard?.description?.fontWeight),
        typography: typography(eChannel?.videoCard?.description?.typography),
        color: singleColor(eChannel?.videoCard?.description?.color),
        visible: boolean(eChannel?.videoCard?.description?.visible),
      },
      dateTime: {
        fontFamily: string(eChannel?.videoCard?.dateTime?.fontFamily),
        fontWeight: fontWeight(eChannel?.videoCard?.dateTime?.fontWeight),
        typography: typography(eChannel?.videoCard?.dateTime?.typography),
        color: singleColor(eChannel?.videoCard?.dateTime?.color),
        visible: boolean(eChannel?.videoCard?.dateTime?.visible),
      },
      duration: {
        fontFamily: string(eChannel?.videoCard?.duration?.fontFamily),
        fontWeight: fontWeight(eChannel?.videoCard?.duration?.fontWeight),
        typography: typography(eChannel?.videoCard?.duration?.typography),
        color: singleColor(eChannel?.videoCard?.duration?.color),
        backgroundColor: singleColor(eChannel?.videoCard?.duration?.backgroundColor),
        visible: boolean(eChannel?.videoCard?.duration?.visible),
      },
      videoEngagement: {
        [VIEW_KEY]: {
          icon: icon(eChannel?.videoCard?.videoEngagement?.[VIEW_KEY]?.icon),
          iconInactive: icon(eChannel?.videoCard?.videoEngagement?.[VIEW_KEY]?.iconInactive),
          fontFamily: string(eChannel?.videoCard?.videoEngagement?.[VIEW_KEY]?.fontFamily),
          fontWeight: fontWeight(eChannel?.videoCard?.videoEngagement?.[VIEW_KEY]?.fontWeight),
          typography: typography(eChannel?.videoCard?.videoEngagement?.[VIEW_KEY]?.typography),
          color: singleColor(eChannel?.videoCard?.videoEngagement?.[VIEW_KEY]?.color),
          colorInactive: singleColor(eChannel?.videoCard?.videoEngagement?.[VIEW_KEY]?.colorInactive),
          visible: boolean(eChannel?.videoCard?.videoEngagement?.[VIEW_KEY]?.visible),
        },
        [LIKE_KEY]: {
          icon: icon(eChannel?.videoCard?.videoEngagement?.[LIKE_KEY]?.icon),
          iconInactive: icon(eChannel?.videoCard?.videoEngagement?.[LIKE_KEY]?.iconInactive),
          fontFamily: string(eChannel?.videoCard?.videoEngagement?.[LIKE_KEY]?.fontFamily),
          fontWeight: fontWeight(eChannel?.videoCard?.videoEngagement?.[LIKE_KEY]?.fontWeight),
          typography: typography(eChannel?.videoCard?.videoEngagement?.[LIKE_KEY]?.typography),
          color: singleColor(eChannel?.videoCard?.videoEngagement?.[LIKE_KEY]?.color),
          colorInactive: singleColor(eChannel?.videoCard?.videoEngagement?.[LIKE_KEY]?.colorInactive),
          visible: boolean(eChannel?.videoCard?.videoEngagement?.[LIKE_KEY]?.visible),
        },
        [SHARE_KEY]: {
          icon: icon(eChannel?.videoCard?.videoEngagement?.[SHARE_KEY]?.icon),
          iconInactive: icon(eChannel?.videoCard?.videoEngagement?.[SHARE_KEY]?.iconInactive),
          fontFamily: string(eChannel?.videoCard?.videoEngagement?.[SHARE_KEY]?.fontFamily),
          fontWeight: fontWeight(eChannel?.videoCard?.videoEngagement?.[SHARE_KEY]?.fontWeight),
          typography: typography(eChannel?.videoCard?.videoEngagement?.[SHARE_KEY]?.typography),
          color: singleColor(eChannel?.videoCard?.videoEngagement?.[SHARE_KEY]?.color),
          colorInactive: singleColor(eChannel?.videoCard?.videoEngagement?.[SHARE_KEY]?.colorInactive),
          visible: boolean(eChannel?.videoCard?.videoEngagement?.[SHARE_KEY]?.visible),
        },
      },
    },
  });

  // Process channels byType
  const externalByType = eConf.modules?.channelList?.channels?.byType || {};

  // eslint-disable-next-line no-restricted-syntax
  for (const design of DESIGN_ENUMS) {
    result.modules.channelList.channels.byType[design] = fillChannel(externalByType[design] as Partial<ChannelConfig>);
  }

  // Process channels byId
  const externalById = eConf.modules?.channelList?.channels?.byId || {};

  // eslint-disable-next-line guard-for-in
  for (const key in externalById) {
    result.modules.channelList.channels.byId[key] = fillChannel(externalById[key]);
  }

  return result;
};

export const deepMergeChannel = <T extends object>(base: T, partial: Partial<T>): T => {
  const result = {} as T;

  for (const key in base) {
    if (!Object.prototype.hasOwnProperty.call(base, key)) continue;

    const baseValue = base[key];
    const partialValue = partial[key];

    if (
      typeof baseValue === 'object' &&
      baseValue !== null &&
      typeof partialValue === 'object' &&
      partialValue !== null
    ) {
      result[key] = deepMergeChannel(
        baseValue as Record<string, unknown>,
        partialValue as Partial<Record<string, unknown>>,
      ) as T[typeof key];
    } else if (partialValue !== undefined) {
      result[key] = partialValue as T[typeof key];
    } else {
      result[key] = baseValue;
    }
  }

  return result;
};

export const isVisibleInConfig = (...flags: Array<boolean | undefined>): boolean =>
  flags.findLast((v): v is boolean => typeof v === 'boolean') ?? true;

export const fixButtonVariant = (
  variant: typeof VARIANT_TEXT | typeof VARIANT_OUTLINED | typeof VARIANT_CONTAINED | typeof VARIANT_FILLED | undefined,
  defaultValue?: ButtonProps['variant'],
): ButtonProps['variant'] => (variant === VARIANT_FILLED ? VARIANT_CONTAINED : (variant ?? defaultValue));
